import { z } from 'zod';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { apiClient } from './client';
import { discoveryKeys, NextStepSchema } from './discovery';
import { newIdempotencyKey } from './engagement-requirements';

/**
 * Engagement -> Discovery -> Findings (T-0188): the evidence ledger
 * (T-0177, E12-F06 §3), an area's findings, follow-ups, evidence from a
 * document and pain points (T-0178, E12-F07 §3).
 *
 * Everything is under `/api/v1/engagements/:engagement_id/discovery`.
 * Coverage, sources, next steps, support counts and the "needs evidence"
 * flags are computed by the server (`@caliber-2/discovery-core`); the
 * screen shows them and never counts. Every write sends an
 * `Idempotency-Key`, one per confirmed action, and refreshes everything
 * Discovery shows (a write can move coverage, the summary and the tabs).
 */

// ---------------------------------------------------------------------------
// The ledger (T-0177)
// ---------------------------------------------------------------------------

export const EVIDENCE_CHANNELS = [
  'interview',
  'questionnaire',
  'follow_up',
  'document',
  'data',
] as const;
export type EvidenceChannel = (typeof EVIDENCE_CHANNELS)[number];

export const EVIDENCE_STATUSES = [
  'proposed',
  'confirmed',
  'declined',
  'withdrawn',
  'superseded',
] as const;
export type EvidenceStatus = (typeof EVIDENCE_STATUSES)[number];

/**
 * What a piece of evidence describes (T-0285, E25-design §3). A tag: it
 * never counts for coverage or any figure. The reader proposes it and a
 * person can change it at any status. A value this screen does not know,
 * or none from an API before T-0285, reads as no label.
 */
export const EVIDENCE_LABELS = [
  'how_it_runs',
  'volumes_timing',
  'systems_files',
  'problems',
  'needs',
] as const;
export type EvidenceLabel = (typeof EVIDENCE_LABELS)[number];
// Typed on the field, as the other tolerant schemas here are, so `apiClient`
// still infers the item's type (`.catch` widens a schema's input to unknown).
const LabelSchema = z
  .enum(EVIDENCE_LABELS)
  .nullable()
  .optional()
  .catch(null) as unknown as z.ZodType<EvidenceLabel | null | undefined>;
/** Who set the label: the AI reader, or a person. */
const LabelSetBySchema = z
  .enum(['ai', 'person'])
  .nullable()
  .optional()
  .catch(null) as unknown as z.ZodType<'ai' | 'person' | null | undefined>;

export const EvidenceSourceSchema = z.object({
  /** `speaker`: a transcript speaker not mapped yet; `interviewer`: never a source. */
  kind: z.enum(['person', 'document', 'speaker', 'interviewer']),
  id: z.string(),
  name: z.string(),
});
export type EvidenceSource = z.infer<typeof EvidenceSourceSchema>;

export const EvidenceCitationSchema = z.object({
  kind: z.enum(EVIDENCE_CHANNELS),
  label: z.string(),
  location: z.string(),
  target: z.record(z.union([z.string(), z.number(), z.null()])),
});
export type EvidenceCitation = z.infer<typeof EvidenceCitationSchema>;

const EvidenceItemSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.enum(EVIDENCE_STATUSES),
  text: z.string(),
  channel: z.enum(EVIDENCE_CHANNELS),
  area_ids: z.array(z.string()),
  areas: z.array(
    z.object({
      area_id: z.string(),
      code: z.string().nullable(),
      label: z.string().nullable(),
      active: z.boolean(),
    }),
  ),
  source: EvidenceSourceSchema.nullable(),
  citation: EvidenceCitationSchema,
  shown_on_screen: z.boolean(),
  label: LabelSchema,
  label_set_by: LabelSetBySchema,
  interview_code: z.string().nullable(),
  confidence: z.number().nullable(),
  /** Proposals only (T-0257): arrives selected for review, the 0.85 rule with nothing blocking it. */
  preselected: z.boolean().optional(),
  hold_reason: z.string().nullable(),
  decline_reason_code: z.string().nullable(),
  decline_reason_text: z.string().nullable(),
  withdraw_reason: z.string().nullable(),
  withdrawn_at: z.string().nullable(),
  created_at: z.string(),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

const LedgerSchema = z.object({
  items: z.array(EvidenceItemSchema),
  /** Counts under every filter but `status`, for the status chips. */
  counts_by_status: z.record(z.number()),
  next_cursor: z.string().nullable(),
});
export type Ledger = z.infer<typeof LedgerSchema>;

export interface LedgerFilters {
  status?: EvidenceStatus;
  channel?: EvidenceChannel;
  untagged?: boolean;
  q?: string;
}

const CoverageChangeSchema = z.object({
  area_id: z.string(),
  label: z.string().nullable(),
  status_before: z.string().nullable(),
  status_after: z.string().nullable(),
});
export type CoverageChange = z.infer<typeof CoverageChangeSchema>;

const WithdrawResultSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.literal('withdrawn'),
  coverage_changes: z.array(CoverageChangeSchema),
  cascade: z.object({
    suggestions_withdrawn: z.array(z.string()),
    pain_points_flagged: z.array(z.object({ id: z.string(), number: z.string().nullable() })),
    map_steps_flagged: z.array(z.object({ step_name: z.string() }).passthrough()),
    requirements_citing: z.array(z.object({ id: z.string(), number: z.string() })),
  }),
});
export type WithdrawResult = z.infer<typeof WithdrawResultSchema>;

const SetAreasResultSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  area_ids: z.array(z.string()),
  coverage_changes: z.array(CoverageChangeSchema),
});
export type SetAreasResult = z.infer<typeof SetAreasResultSchema>;

// ---------------------------------------------------------------------------
// An area's findings (T-0178)
// ---------------------------------------------------------------------------

/** One piece of evidence as a pain point, a suggestion or the area page quotes it. */
const QuotedEvidenceSchema = z.object({
  evidence_id: z.string(),
  number: z.string().nullable(),
  text: z.string(),
  status: z.enum(EVIDENCE_STATUSES),
  source: EvidenceSourceSchema.nullable(),
  citation: EvidenceCitationSchema,
  label: LabelSchema,
  label_set_by: LabelSetBySchema,
  /** The area page's items only. */
  shown_on_screen: z.boolean().optional(),
  source_key: z.string().nullable().optional(),
  withdraw_reason: z.string().nullable().optional(),
  withdrawn_at: z.string().nullable().optional(),
});
export type QuotedEvidence = z.infer<typeof QuotedEvidenceSchema>;
export { QuotedEvidenceSchema };

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];

const PainPointSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  text: z.string(),
  status: z.enum(['proposed', 'confirmed', 'dismissed']),
  origin: z.enum(['ai', 'manual']),
  severity: z.enum(SEVERITIES).nullable(),
  /** The AI's hint, never applied (rule 18). */
  ai_suggested_severity: z.enum(SEVERITIES).nullable(),
  area_ids: z.array(z.string()),
  evidence: z.array(QuotedEvidenceSchema),
  sources_count: z.number(),
  /** None of its evidence is still confirmed (Decision 54.20), computed. */
  needs_attention: z.boolean(),
  dismissed_from: z.enum(['proposed', 'confirmed']).nullable(),
  dismiss_reason: z.string().nullable(),
  dismissed_at: z.string().nullable(),
  created_at: z.string(),
});
export type PainPoint = z.infer<typeof PainPointSchema>;

/**
 * An interview evidence waits in: `{ id, code }`. An API before 19 Oct 2026
 * sent the code alone, read as `id: null`. Typed on the item, as the other
 * preprocessed schemas are, so `apiClient` still infers the area's type.
 */
const WaitingInterviewSchema = z.preprocess(
  (i) => (typeof i === 'string' ? { id: null, code: i } : i),
  z.object({ id: z.string().nullable(), code: z.string() }),
) as unknown as z.ZodType<{ id: string | null; code: string }>;

const AreaFindingsSchema = z.object({
  area: z.object({
    area_id: z.string(),
    code: z.string(),
    label: z.string(),
    name: z.string(),
    definition: z.string().nullable(),
  }),
  coverage: z.object({
    status: z.enum(['corroborated', 'partial', 'none']),
    status_label: z.string(),
    sources: z.number(),
    required: z.number(),
  }),
  next_step: NextStepSchema,
  sources: z.array(
    z.object({
      key: z.string(),
      kind: z.enum(['person', 'document']),
      id: z.string(),
      name: z.string(),
      /** Counts as a source (confirmed); false while only proposals stand behind it. */
      counted: z.boolean(),
      confirmed_count: z.number(),
    }),
  ),
  pain_points: z.array(PainPointSchema),
  /** Confirmed evidence, and withdrawn items struck through (they never count). */
  said: z.array(QuotedEvidenceSchema),
  /** Proposals still awaiting review, and the interviews and documents they come from. */
  waiting: z.object({
    count: z.number(),
    interviews: z.array(WaitingInterviewSchema),
    /** The documents the evidence finder read (T-0257); an older API sends none. */
    documents: z
      .array(z.object({ id: z.string(), title: z.string(), proposal_count: z.number() }))
      .optional(),
  }),
  processes: z.array(
    z.object({
      process_id: z.string(),
      name: z.string(),
      map_state: z.enum(['validated', 'draft', 'ready', 'waiting']),
    }),
  ),
});
export type AreaFindings = z.infer<typeof AreaFindingsSchema>;

// ---------------------------------------------------------------------------
// Evidence from a document, follow-ups (T-0178)
// ---------------------------------------------------------------------------

const PassageSchema = z.object({
  page: z.number(),
  page_count: z.number().nullable(),
  /** Null when the page has no parsed text. */
  text: z.string().nullable(),
});

export interface DocumentEvidenceBody {
  document_id: string;
  page?: number;
  sheet?: string;
  row_from?: number;
  row_to?: number;
  text: string;
  area_ids: string[];
}

const DocumentEvidenceResultSchema = z.object({
  id: z.string(),
  number: z.string(),
  coverage_changes: z.array(CoverageChangeSchema),
  /** Decision 54.25: saved either way; the page did not contain it, or could not be read. */
  warning: z.enum(['passage_not_found_on_page', 'passage_not_checked']).optional(),
});
export type DocumentEvidenceResult = z.infer<typeof DocumentEvidenceResultSchema>;

const FollowUpDraftSchema = z.object({
  /** People who are not a source for the area yet (rule 19). */
  candidates: z.array(
    z.object({
      person_id: z.string(),
      full_name: z.string(),
      title: z.string().nullable(),
      email: z.string().nullable(),
      /** Interviews they attend that are not held yet, for "Add to their next interview". */
      next_interviews: z.array(
        z.object({ interview_id: z.string(), code: z.string(), title: z.string() }),
      ),
    }),
  ),
  questions: z.array(z.object({ question_id: z.string(), code: z.string(), text: z.string() })),
  /**
   * The AI questions for the area, each with its reason: the area's own gap
   * question (Decision 53.13, no `step`), then the question of each step of
   * its process maps that nobody has described (T-0286), naming the step.
   */
  ai_questions: z.array(
    z.object({
      ai_proposal_id: z.string(),
      text: z.string(),
      why: z.string(),
      step: z
        .object({ key: z.string(), name: z.string(), process_map_id: z.string() })
        .nullable()
        .optional(),
    }),
  ),
  max_questions: z.number(),
});
export type FollowUpDraft = z.infer<typeof FollowUpDraftSchema>;

export type FollowUpQuestion =
  | { question_id: string }
  | { ai_proposal_id: string }
  | { text: string };

export interface FollowUpBody {
  area_id: string;
  person_id: string;
  questions: FollowUpQuestion[];
  channel: 'link' | 'next_interview';
  interview_id?: string;
}

const FollowUpResultSchema = z.object({
  id: z.string(),
  link_id: z.string().nullable().optional(),
});

/** 1 to 8 questions per follow-up (rule 19). */
export const FOLLOW_UP_MAX_QUESTIONS = 8;

const RunSchema = z.object({ run_id: z.string(), joined: z.boolean().optional() });
const ConfirmedPainPointSchema = z.object({ number: z.string() });
const AddedPainPointSchema = z.object({ id: z.string(), number: z.string() });

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function base(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/discovery`;
}

const LEDGER_PAGE = 200;

export const listEvidence = (engagementId: string, f: LedgerFilters, cursor?: string) =>
  apiClient(`${base(engagementId)}/evidence`, LedgerSchema, {
    query: {
      status: f.status,
      channel: f.channel,
      untagged: f.untagged ? 'true' : undefined,
      q: f.q?.trim() ? f.q.trim() : undefined,
      cursor,
      limit: LEDGER_PAGE,
    },
  });

/**
 * Every ledger item from one document, for the document review panel
 * (T-0257): the proposals the evidence finder made and what was confirmed
 * from it. One page: a document holds tens of items, not thousands.
 */
export const listDocumentEvidence = (engagementId: string, documentId: string) =>
  apiClient(`${base(engagementId)}/evidence`, LedgerSchema, {
    query: { source: `doc:${documentId}`, limit: 500 },
  });

export const withdrawEvidence = (engagementId: string, id: string, reason: string, key: string) =>
  apiClient(
    `${base(engagementId)}/evidence/${encodeURIComponent(id)}:withdraw`,
    WithdrawResultSchema,
    {
      method: 'POST',
      body: { reason },
      idempotencyKey: key,
    },
  );

/** T-0285 — say what a piece of evidence describes, or clear it (`null`). */
export const setEvidenceLabel = (engagementId: string, id: string, label: EvidenceLabel | null) =>
  apiClient(`${base(engagementId)}/evidence/${encodeURIComponent(id)}/label`, z.unknown(), {
    method: 'PUT',
    body: { label },
    idempotencyKey: newIdempotencyKey(),
  });

/**
 * T-0285 — ask AI to label the engagement's confirmed evidence that has no
 * label yet and that no person has labelled. The engagement lead or a firm
 * admin only. One run at a time; a second request joins it; with nothing
 * to label the API starts nothing and says so.
 */
export const labelEvidence = (engagementId: string) =>
  apiClient(`${base(engagementId)}/evidence:label`, RunSchema, {
    method: 'POST',
    body: {},
    idempotencyKey: newIdempotencyKey(),
  });

/**
 * T-0285 — where labelling earlier evidence stands: the run in flight, how
 * the last one ended, and whether this person may start one.
 */
const EvidenceLabellingSchema = z.object({
  unlabelled_count: z.number(),
  can_start: z.boolean().optional(),
  active_run: z.object({ run_id: z.string(), status: z.string() }).nullable(),
  last_run: z
    .object({
      run_id: z.string(),
      status: z.enum(['completed', 'failed']),
      labelled_count: z.number(),
      error_message: z.string().nullable(),
    })
    .nullable(),
});
export type EvidenceLabelling = z.infer<typeof EvidenceLabellingSchema>;

export const getEvidenceLabelling = (engagementId: string) =>
  apiClient(`${base(engagementId)}/evidence:label`, EvidenceLabellingSchema);

export const setEvidenceAreas = (engagementId: string, id: string, areaIds: string[]) =>
  apiClient(
    `${base(engagementId)}/evidence/${encodeURIComponent(id)}/areas`,
    SetAreasResultSchema,
    {
      method: 'PUT',
      body: { area_ids: areaIds },
      idempotencyKey: newIdempotencyKey(),
    },
  );

export const getAreaFindings = (engagementId: string, areaId: string) =>
  apiClient(`${base(engagementId)}/areas/${encodeURIComponent(areaId)}`, AreaFindingsSchema);

export const getDocumentPassage = (engagementId: string, documentId: string, page: number) =>
  apiClient(
    `${base(engagementId)}/documents/${encodeURIComponent(documentId)}/passage`,
    PassageSchema,
    { query: { page } },
  );

export const addDocumentEvidence = (
  engagementId: string,
  body: DocumentEvidenceBody,
  key: string,
) =>
  apiClient(`${base(engagementId)}/evidence`, DocumentEvidenceResultSchema, {
    method: 'POST',
    body,
    idempotencyKey: key,
  });

export const getFollowUpDraft = (engagementId: string, areaId: string) =>
  apiClient(`${base(engagementId)}/follow-ups/draft`, FollowUpDraftSchema, {
    query: { area_id: areaId },
  });

export const sendFollowUp = (engagementId: string, body: FollowUpBody, key: string) =>
  apiClient(`${base(engagementId)}/follow-ups`, FollowUpResultSchema, {
    method: 'POST',
    body,
    idempotencyKey: key,
  });

export const lookAgainForPainPoints = (engagementId: string) =>
  apiClient(`${base(engagementId)}/pain-points:propose`, RunSchema, {
    method: 'POST',
    body: {},
    idempotencyKey: newIdempotencyKey(),
  });

export interface ManualPainPointBody {
  text: string;
  area_ids: string[];
  evidence_ids: string[];
  severity: Severity;
}

export const addPainPoint = (engagementId: string, body: ManualPainPointBody, key: string) =>
  apiClient(`${base(engagementId)}/pain-points`, AddedPainPointSchema, {
    method: 'POST',
    body,
    idempotencyKey: key,
  });

/**
 * Pain points with no active area of the pinned version (an AI proposal
 * stored with none, or areas retired by a version move): no area page lists
 * them, but the sign-off checklist still counts a proposed one.
 */
export const listUntaggedPainPoints = (engagementId: string) =>
  apiClient(`${base(engagementId)}/pain-points`, z.array(PainPointSchema), {
    query: { untagged: 'true' },
  });

const painPoint = (engagementId: string, id: string) =>
  `${base(engagementId)}/pain-points/${encodeURIComponent(id)}`;

/** Replace a pain point's areas: at least one, each active in the pinned version. */
export const setPainPointAreas = (engagementId: string, id: string, areaIds: string[]) =>
  apiClient(painPoint(engagementId, id), z.unknown(), {
    method: 'PATCH',
    body: { area_ids: areaIds },
    idempotencyKey: newIdempotencyKey(),
  });

export const setPainPointSeverity = (engagementId: string, id: string, severity: Severity) =>
  apiClient(painPoint(engagementId, id), z.unknown(), {
    method: 'PATCH',
    body: { severity },
    idempotencyKey: newIdempotencyKey(),
  });

export const confirmPainPoint = (engagementId: string, id: string, severity: Severity) =>
  apiClient(`${painPoint(engagementId, id)}:confirm`, ConfirmedPainPointSchema, {
    method: 'POST',
    body: { severity },
    idempotencyKey: newIdempotencyKey(),
  });

/** Decision 54.24: the reasons for dismissing a confirmed pain point; `other` takes text. */
export const PAIN_DISMISS_REASONS = [
  'not_a_real_problem',
  'covered_by_another',
  'out_of_scope',
  'other',
] as const;
export type PainDismissReason = (typeof PAIN_DISMISS_REASONS)[number];

export const dismissPainPoint = (
  engagementId: string,
  id: string,
  reason: { code: PainDismissReason; text?: string } | null,
) =>
  apiClient(`${painPoint(engagementId, id)}:dismiss`, z.unknown(), {
    method: 'POST',
    body: reason ? { reason: reason.code, ...(reason.text ? { text: reason.text } : {}) } : {},
    idempotencyKey: newIdempotencyKey(),
  });

/** Link more confirmed evidence, e.g. to a pain point whose evidence was withdrawn (54.20). */
export const linkPainPointEvidence = (engagementId: string, id: string, evidenceIds: string[]) =>
  apiClient(`${painPoint(engagementId, id)}:link-evidence`, z.unknown(), {
    method: 'POST',
    body: { evidence_ids: evidenceIds },
    idempotencyKey: newIdempotencyKey(),
  });

/** Ask AI to draft the area's gap question now (it also runs when coverage changes). */
export const requestGapQuestion = (engagementId: string, areaId: string) =>
  apiClient(
    `${base(engagementId)}/areas/${encodeURIComponent(areaId)}/gap-questions:propose`,
    RunSchema,
    {
      method: 'POST',
      body: {},
      idempotencyKey: newIdempotencyKey(),
    },
  );

export const restorePainPoint = (engagementId: string, id: string) =>
  apiClient(`${painPoint(engagementId, id)}:restore`, z.unknown(), {
    method: 'POST',
    body: {},
    idempotencyKey: newIdempotencyKey(),
  });

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const findingsKeys = {
  ledger: (engagementId: string, f: LedgerFilters) =>
    ['discovery', engagementId, 'ledger', f] as const,
  area: (engagementId: string, areaId: string) =>
    ['discovery', engagementId, 'area', areaId] as const,
  document: (engagementId: string, documentId: string) =>
    ['discovery', engagementId, 'document-evidence', documentId] as const,
  untaggedPainPoints: (engagementId: string) =>
    ['discovery', engagementId, 'pain-points', 'untagged'] as const,
  followUpDraft: (engagementId: string, areaId: string) =>
    ['discovery', engagementId, 'follow-up-draft', areaId] as const,
  passage: (engagementId: string, documentId: string, page: number) =>
    ['discovery', engagementId, 'passage', documentId, page] as const,
  labelling: (engagementId: string) => ['discovery', engagementId, 'evidence-labelling'] as const,
};

export function useLedgerQuery(engagementId: string, f: LedgerFilters) {
  return useInfiniteQuery({
    queryKey: findingsKeys.ledger(engagementId, f),
    queryFn: ({ pageParam }) => listEvidence(engagementId, f, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: false,
  });
}

/**
 * One area's findings. `poll` asks again every few seconds, for a short
 * while after "Look again" while AI reads in the background.
 */
export function useAreaFindingsQuery(engagementId: string, areaId: string, poll = false) {
  return useQuery({
    queryKey: findingsKeys.area(engagementId, areaId),
    queryFn: () => getAreaFindings(engagementId, areaId),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval: poll ? 4000 : false,
    retry: false,
  });
}

/** The ledger items of one document, for its review panel (T-0257). */
export function useDocumentEvidenceQuery(engagementId: string, documentId: string | null) {
  return useQuery({
    queryKey: findingsKeys.document(engagementId, documentId ?? ''),
    queryFn: () => listDocumentEvidence(engagementId, documentId!),
    enabled: !!documentId,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
}

/** Pain points no area page lists; Findings and the sign-off checklist read them. */
export function useUntaggedPainPointsQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: findingsKeys.untaggedPainPoints(engagementId),
    queryFn: () => listUntaggedPainPoints(engagementId),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

/** `poll` asks again every few seconds while AI drafts the gap question. */
export function useFollowUpDraftQuery(engagementId: string, areaId: string, poll = false) {
  return useQuery({
    queryKey: findingsKeys.followUpDraft(engagementId, areaId),
    queryFn: () => getFollowUpDraft(engagementId, areaId),
    refetchInterval: poll ? 4000 : false,
    retry: false,
  });
}

export function usePassageQuery(engagementId: string, documentId: string, page: number | null) {
  return useQuery({
    queryKey: findingsKeys.passage(engagementId, documentId, page ?? 0),
    queryFn: () => getDocumentPassage(engagementId, documentId, page!),
    enabled: Boolean(documentId) && page !== null && page > 0,
    staleTime: 60_000,
    retry: false,
  });
}

/** Where labelling stands (T-0285). Asks again every few seconds while a run is in flight. */
export function useEvidenceLabellingQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: findingsKeys.labelling(engagementId),
    queryFn: () => getEvidenceLabelling(engagementId),
    enabled,
    staleTime: 15_000,
    refetchInterval: (q) => (q.state.data?.active_run ? 3000 : false),
    retry: false,
  });
}

/** Every write refreshes all of Discovery: coverage, the summary and the tabs can move. */
function useDiscoveryWrite<TVars, TResult>(
  engagementId: string,
  fn: (vars: TVars) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: discoveryKeys.all(engagementId) }),
  });
}

export const useWithdrawEvidence = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (v: { id: string; reason: string; key: string }) =>
    withdrawEvidence(engagementId, v.id, v.reason, v.key),
  );

export const useSetEvidenceAreas = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (v: { id: string; areaIds: string[] }) =>
    setEvidenceAreas(engagementId, v.id, v.areaIds),
  );

export const useSetEvidenceLabel = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (v: { id: string; label: EvidenceLabel | null }) =>
    setEvidenceLabel(engagementId, v.id, v.label),
  );

export const useLabelEvidence = (engagementId: string) =>
  useDiscoveryWrite(engagementId, () => labelEvidence(engagementId));

export const useAddDocumentEvidence = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (v: { body: DocumentEvidenceBody; key: string }) =>
    addDocumentEvidence(engagementId, v.body, v.key),
  );

export const useSendFollowUp = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (v: { body: FollowUpBody; key: string }) =>
    sendFollowUp(engagementId, v.body, v.key),
  );

export const useLookAgain = (engagementId: string) =>
  useDiscoveryWrite(engagementId, () => lookAgainForPainPoints(engagementId));

export const useAddPainPoint = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (v: { body: ManualPainPointBody; key: string }) =>
    addPainPoint(engagementId, v.body, v.key),
  );

export const useRequestGapQuestion = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (areaId: string) => requestGapQuestion(engagementId, areaId));

export type PainPointAction =
  | { kind: 'severity'; id: string; severity: Severity }
  | { kind: 'confirm'; id: string; severity: Severity }
  | { kind: 'dismiss'; id: string; reason: { code: PainDismissReason; text?: string } | null }
  | { kind: 'restore'; id: string }
  | { kind: 'link'; id: string; evidenceIds: string[] }
  | { kind: 'areas'; id: string; areaIds: string[] };

export const usePainPointAction = (engagementId: string) =>
  useDiscoveryWrite(engagementId, (a: PainPointAction): Promise<unknown> => {
    switch (a.kind) {
      case 'severity':
        return setPainPointSeverity(engagementId, a.id, a.severity);
      case 'confirm':
        return confirmPainPoint(engagementId, a.id, a.severity);
      case 'dismiss':
        return dismissPainPoint(engagementId, a.id, a.reason);
      case 'restore':
        return restorePainPoint(engagementId, a.id);
      case 'link':
        return linkPainPointEvidence(engagementId, a.id, a.evidenceIds);
      case 'areas':
        return setPainPointAreas(engagementId, a.id, a.areaIds);
    }
  });
